import { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import {
  Sprout,
  BarChart3,
  CloudSun,
  Handshake,
  Users,
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  ChevronDown,
  Leaf,
  TrendingUp,
  CheckCircle2 } from
"lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Card, CardContent } from "./components/ui/card";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Images from design guidelines
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?q=80&w=2232&auto=format&fit=crop",
  about: "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?q=80&w=2070&auto=format&fit=crop",
  services: "https://images.unsplash.com/photo-1574943320219-553eb213f72d?q=80&w=2091&auto=format&fit=crop",
  impact: "https://images.unsplash.com/photo-1595841696677-6489ff3f8cd1?q=80&w=2070&auto=format&fit=crop",
  founder1: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=987&auto=format&fit=crop",
  founder2: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=2070&auto=format&fit=crop"
};

// Navigation Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#impact", label: "Impact" },
  { href: "#founders", label: "Founders" },
  { href: "#testimonials", label: "Reviews" },
  { href: "#contact", label: "Contact" }];


  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ?
      "bg-white/95 backdrop-blur-md shadow-sm" :
      "bg-transparent"}`
      }
      data-testid="navbar">

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="#"
            className="flex items-center gap-2"
            data-testid="logo-link">

            <Sprout className={`w-8 h-8 ${isScrolled ? "text-[#1a4d2e]" : "text-white"}`} />
            <span className={`font-bold text-lg md:text-xl font-['Manrope'] ${
            isScrolled ? "text-[#1a4d2e]" : "text-white"}`
            }>
              Sarthesha FPC
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
            <a
              key={link.href}
              href={link.href}
              className={`text-sm font-medium transition-colors hover:text-[#1a4d2e] ${
              isScrolled ? "text-slate-600" : "text-white/90 hover:text-white"}`
              }
              data-testid={`nav-${link.label.toLowerCase()}`}>

                {link.label}
              </a>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn">

            {isMobileMenuOpen ?
            <X className={isScrolled ? "text-[#1a4d2e]" : "text-white"} /> :

            <Menu className={isScrolled ? "text-[#1a4d2e]" : "text-white"} />
            }
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen &&
        <div className="md:hidden bg-white rounded-lg shadow-lg mt-2 p-4 animate-fade-in">
            {navLinks.map((link) =>
          <a
            key={link.href}
            href={link.href}
            className="block py-3 text-slate-600 hover:text-[#1a4d2e] border-b border-slate-100 last:border-0"
            onClick={() => setIsMobileMenuOpen(false)}
            data-testid={`mobile-nav-${link.label.toLowerCase()}`}>

                {link.label}
              </a>
          )}
          </div>
        }
      </div>
    </nav>);

};

// Hero Section
const HeroSection = () => {
  return (
    <section
      className="hero-section relative min-h-screen flex items-center"
      data-testid="hero-section">

      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${IMAGES.hero})` }} />

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0f2f1a]/80 via-[#1a4d2e]/70 to-[#0f2f1a]/90" />
      
      {/* Content */}
      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 mb-6 opacity-0 animate-fade-in-up">
            <Leaf className="w-5 h-5 text-[#90ee90]" />
            <span className="text-[#90ee90] text-sm font-medium uppercase tracking-wider">
              Agriculture Technology
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Manrope'] leading-tight opacity-0 animate-fade-in-up animation-delay-200">
            Cultivating the Future of Indian Agriculture
          </h1>
          
          <p className="text-lg md:text-xl text-white/85 mb-8 leading-relaxed max-w-2xl opacity-0 animate-fade-in-up animation-delay-400">
            Empowering Maharashtra's farmers with data-driven insights, AI technology, and sustainable market connections.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in-up animation-delay-600">
            <Button
              className="bg-white text-[#1a4d2e] hover:bg-[#f0fdf4] rounded-full px-8 py-6 text-base font-medium shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
              data-testid="hero-cta-btn"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>

              Get in Touch
            </Button>
            <Button
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-base font-medium"
              data-testid="hero-learn-btn"
              onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}>

              Learn More
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="w-8 h-8 text-white/70" />
      </div>
    </section>);

};

// About Section
const AboutSection = () => {
  return (
    <section
      id="about"
      className="py-20 md:py-28 bg-white"
      data-testid="about-section">

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          {/* Image */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={IMAGES.about}
                alt="Indian farmers in field"
                className="w-full h-[400px] object-cover" />

            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#1a4d2e] text-white p-6 rounded-2xl shadow-xl hidden md:block">
              <p className="text-3xl font-bold font-['Manrope']">2024</p>
              <p className="text-sm text-white/80">Founded</p>
            </div>
          </div>

          {/* Content */}
          <div>
            <span className="text-[#1a4d2e] text-sm font-semibold uppercase tracking-wider">
              Our Story
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-6 font-['Manrope']">
              Bridging Tradition & Technology
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-6">
              Founded by Shreyash Kale and Sarthak Shah, Sarthesha Farmers Producer Company 
              is dedicated to solving the economic challenges faced by rural farmers in Maharashtra.
            </p>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              We bridge the gap between traditional farming wisdom and modern technology, 
              empowering farmers with data-driven tools to make informed decisions about 
              their crops, timing, and markets.
            </p>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#e9f5db] flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-[#1a4d2e]" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Data-Driven</p>
                  <p className="text-sm text-slate-500">AI-powered insights</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#e9f5db] flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-[#1a4d2e]" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Community First</p>
                  <p className="text-sm text-slate-500">Supporting farmers</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

// Services Section
const ServicesSection = () => {
  const services = [
  {
    icon: BarChart3,
    title: "Crop Price Prediction",
    description: "AI-powered forecasting using machine learning to help farmers sell at the right time and maximize their returns."
  },
  {
    icon: CloudSun,
    title: "Smart Advisory",
    description: "Real-time guidance based on weather patterns, soil health data, and regional conditions for optimal farming decisions."
  },
  {
    icon: Handshake,
    title: "Market Linkage",
    description: "Direct connection to buyers and markets, eliminating middlemen and ensuring fair prices for farmers."
  }];


  return (
    <section
      id="services"
      className="py-20 md:py-28 bg-[#f8fafc]"
      data-testid="services-section">

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#1a4d2e] text-sm font-semibold uppercase tracking-wider">
            What We Do
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4 font-['Manrope']">
            Technology for Farmers
          </h2>
          <p className="text-slate-600 text-lg">
            We combine artificial intelligence with agricultural expertise to provide 
            farmers with actionable insights.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) =>
          <Card
            key={index}
            className="service-card bg-white border-slate-100 hover:shadow-xl hover:border-[#1a4d2e]/20 transition-all duration-300"
            data-testid={`service-card-${index}`}>

              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#e9f5db] to-[#d4edda] flex items-center justify-center mb-6">
                  <service.icon className="w-8 h-8 text-[#1a4d2e]" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3 font-['Manrope']">
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {service.description}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </section>);

};

// Impact Section
const ImpactSection = () => {
  const stats = [
  { number: "30+", label: "Farmers Supported", icon: Users },
  { number: "100%", label: "Local Focus", icon: MapPin },
  { number: "24/7", label: "Support Available", icon: Phone }];


  return (
    <section
      id="impact"
      className="py-20 md:py-28 relative overflow-hidden"
      data-testid="impact-section">

      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${IMAGES.impact})` }} />

      <div className="absolute inset-0 bg-[#1a4d2e]/90" />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#90ee90] text-sm font-semibold uppercase tracking-wider">
            Our Impact
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-4 font-['Manrope']">
            Making a Difference in Maharashtra
          </h2>
          <p className="text-white/80 text-lg">
            We've supported over 30 farmers in Maharashtra by helping them make 
            informed farming and market decisions.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {stats.map((stat, index) =>
          <div
            key={index}
            className="text-center p-8 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20"
            data-testid={`impact-stat-${index}`}>

              <stat.icon className="w-10 h-10 text-[#90ee90] mx-auto mb-4" />
              <p className="text-5xl md:text-6xl font-bold text-white mb-2 font-['Manrope']">
                {stat.number}
              </p>
              <p className="text-white/80 text-lg">{stat.label}</p>
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Founders Section
const FoundersSection = () => {
  const founders = [
  {
    name: "Shreyash Kale",
    role: "Co-Founder & CEO",
    initials: "SK",
    bio: "Shreyash Kale co-founded Sarthesha with the vision of applying data analytics and artificial intelligence to address real-world challenges faced by farmers in India. Alongside developing technology-driven solutions, he works directly with farming communities to understand on-ground problems and help translate data insights into practical agricultural decisions. His goal is to bridge the gap between traditional farming practices and modern technological solutions to improve farmer livelihoods."
  },
  {
    name: "Sarthak Shah",
    role: "Co-Founder & COO",
    initials: "SS",
    bio: "Sarthak Shah co-founded Sarthesha with a strong interest in understanding agricultural markets and farmer livelihoods. Through close interaction with farming communities, he focuses on learning how local crop markets operate, including pricing patterns, demand cycles, and distribution channels. His work helps ensure that insights from the field are incorporated into Sarthesha's initiatives so that farmers can make more informed decisions about crop planning and selling their produce."
  }];


  return (
    <section
      id="founders"
      className="py-20 md:py-28 bg-white"
      data-testid="founders-section">

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#1a4d2e] text-sm font-semibold uppercase tracking-wider">
            Leadership
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4 font-['Manrope']">
            Meet the Founders
          </h2>
          <p className="text-slate-600 text-lg">
            Entrepreneurs committed to transforming Indian agriculture through technology and data-driven insights.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {founders.map((founder, index) =>
          <Card
            key={index}
            className="bg-white border border-slate-100 hover:shadow-lg hover:border-[#1a4d2e]/20 transition-all duration-300"
            data-testid={`founder-card-${index}`}>

              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1a4d2e] to-[#2d6a4f] flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xl font-bold font-['Manrope']">
                      {founder.initials}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 font-['Manrope']">
                      {founder.name}
                    </h3>
                    <p className="text-[#1a4d2e] font-medium">{founder.role}</p>
                  </div>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {founder.bio}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </section>);

};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
  {
    name: "\u092F\u0936\u094B\u0926\u093F\u092A \u0930\u0923\u0938\u093F\u0902\u0917",
    nameEn: "Ramesh Patil",
    location: "\u0936\u093F\u0915\u094D\u0930\u093E\u092A\u0942\u0930, \u092E\u0939\u093E\u0930\u093E\u0937\u094D\u091F\u094D\u0930",
    text: "सार्थेशाच्या मदतीने मला माझ्या पिकाला योग्य भाव मिळाला. त्यांच्या तंत्रज्ञानामुळे मला बाजारातील किंमतींचा अंदाज वेळेवर कळला.",
    textEn: "With Sarthesha's help, I got the right price for my crops. Their technology helped me predict market prices on time."
  },
  {
    name: "सुरेश देशमुख",
    nameEn: "Suresh Deshmukh",
    location: "\u0936\u093F\u0930\u0942\u0930, \u092E\u0939\u093E\u0930\u093E\u0937\u094D\u091F\u094D\u0930",
    text: "\u0939\u0935\u093E\u092E\u093E\u0928 \u0906\u0923\u093F \u092E\u093E\u0924\u0940\u091A\u094D\u092F\u093E \u092E\u093E\u0939\u093F\u0924\u0940\u091A\u094D\u092F\u093E \u0906\u0927\u093E\u0930\u0947 \u092A\u093F\u0915 \u0915\u0938\u0902 \u0928\u093F\u0935\u0921\u093E\u092F\u091A\u0902 \u092F\u093E\u092C\u0926\u094D\u0926\u0932 \u0938\u093E\u0930\u094D\u0925\u0947\u0936\u093E \u0915\u0921\u0942\u0928 \u091A\u093E\u0902\u0917\u0932\u0902 \u092E\u093E\u0930\u094D\u0917\u0926\u0930\u094D\u0936\u0928 \u092E\u093F\u0933\u093E\u0932\u0902.",
    textEn: "Sarthesha provided useful guidance on how weather and soil information can help farmers choose the right crops."
  },
  {
    name: "\u0905\u0936\u094B\u0915 \u092A\u093E\u091F\u0940\u0932",
    nameEn: "Ashok Jadhav",
    location: "\u0935\u0930\u0941\u0921\u0947, \u092E\u0939\u093E\u0930\u093E\u0937\u094D\u091F\u094D\u0930",
    text: "\u0938\u093E\u0930\u094D\u0925\u0947\u0936\u093E \u092E\u0941\u0933\u0947 \u092C\u093E\u091C\u093E\u0930\u093E\u0924\u0940\u0932 \u092E\u093E\u0917\u0923\u0940 \u0906\u0923\u093F \u092D\u093E\u0935 \u0938\u092E\u091C\u093E\u092F\u0932\u093E \u092E\u0926\u0924 \u091D\u093E\u0932\u0940. \u0924\u094D\u092F\u093E\u092E\u0941\u0933\u0947 \u0928\u093F\u0930\u094D\u0923\u092F \u0918\u0947\u0923\u0902 \u0938\u094B\u092A\u0902 \u091D\u093E\u0932\u0902.",
    textEn: "Sarthesha helped us understand market demand and crop prices, which made it easier to make farming decisions."
  }];


  return (
    <section
      id="testimonials"
      className="py-20 md:py-28 bg-[#f8fafc]"
      data-testid="testimonials-section">

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#1a4d2e] text-sm font-semibold uppercase tracking-wider">
            शेतकऱ्यांचे अनुभव
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4 font-['Manrope']">
            Farmer Reviews
          </h2>
          <p className="text-slate-600 text-lg">
            Hear from the farmers we've had the privilege to support.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) =>
          <div
            key={index}
            className="testimonial-card bg-white p-8 rounded-2xl border-l-4 border-[#1a4d2e] relative shadow-sm hover:shadow-lg transition-shadow"
            data-testid={`testimonial-card-${index}`}>

              <div className="text-6xl text-[#1a4d2e]/10 font-serif absolute top-4 left-6">"</div>
              <p className="text-slate-700 leading-relaxed mb-4 relative z-10 italic">
                {testimonial.text}
              </p>
              <p className="text-slate-500 text-sm mb-6 relative z-10">
                {testimonial.textEn}
              </p>
              <div className="flex items-center border-t border-slate-100 gap-3 pt-4">
                <div className="w-12 h-12 rounded-full bg-[#e9f5db] flex items-center justify-center">
                  <span className="text-[#1a4d2e] font-semibold text-lg">
                    {testimonial.nameEn.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{testimonial.name}</p>
                  <p className="text-sm text-slate-500">{testimonial.location}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post("https://formspree.io/f/mojkqjzj", formData, {
        headers: {
          'Accept': 'application/json'
        }
      });
      if (response.status === 200) {
        toast.success("Message sent successfully! We'll get back to you soon.");
        setFormData({ name: "", email: "", phone: "", message: "" });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-20 md:py-28 bg-white"
      data-testid="contact-section">

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16">
          {/* Contact Info */}
          <div>
            <span className="text-[#1a4d2e] text-sm font-semibold uppercase tracking-wider">
              Get in Touch
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-6 font-['Manrope']">
              Contact Us
            </h2>
            <p className="text-slate-600 text-lg mb-8">
              Have questions about our services? Want to learn how we can help your 
              farming operations? Reach out to us.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#e9f5db] flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#1a4d2e]" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Email</p>
                  <a
                    href="mailto:shreyashkale3939@gmail.com"
                    className="text-[#1a4d2e] hover:underline"
                    data-testid="contact-email">shreyashkale3939@gmail.com
shahsarthak9604@gmail.com

                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#e9f5db] flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#1a4d2e]" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Phone</p>
                  <p className="text-slate-600">
                    <a href="tel:+919527005006" className="hover:text-[#1a4d2e]" data-testid="contact-phone-1">
                      +91 9527005006
                    </a>
                    <span className="text-slate-400"> - Shreyash Kale</span>
                  </p>
                  <p className="text-slate-600">
                    <a href="tel:+919881763939" className="hover:text-[#1a4d2e]" data-testid="contact-phone-2">
                      +91 9881763939
                    </a>
                    <span className="text-slate-400"> - Sumitra Kale</span>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#e9f5db] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#1a4d2e]" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Location</p>
                  <p className="text-slate-600">Maharashtra, India</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[#f8fafc] p-8 rounded-2xl">
            <h3 className="text-xl font-semibold text-slate-900 mb-6 font-['Manrope']">
              Send us a Message
            </h3>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name *
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  required
                  className="bg-white border-slate-200 focus:border-[#1a4d2e] focus:ring-[#1a4d2e]"
                  data-testid="contact-name-input" />

              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address *
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  required
                  className="bg-white border-slate-200 focus:border-[#1a4d2e] focus:ring-[#1a4d2e]"
                  data-testid="contact-email-input" />

              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Phone Number
                </label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 XXXXX XXXXX"
                  className="bg-white border-slate-200 focus:border-[#1a4d2e] focus:ring-[#1a4d2e]"
                  data-testid="contact-phone-input" />

              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Message *
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="How can we help you?"
                  required
                  rows={4}
                  className="bg-white border-slate-200 focus:border-[#1a4d2e] focus:ring-[#1a4d2e]"
                  data-testid="contact-message-input" />

              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#1a4d2e] hover:bg-[#143d23] text-white rounded-full py-6 font-medium transition-all hover:-translate-y-1"
                data-testid="contact-submit-btn">

                {isSubmitting ?
                <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </span> :

                <span className="flex items-center justify-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Send Message
                  </span>
                }
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>);

};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#0f2f1a] text-white py-12" data-testid="footer">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Sprout className="w-8 h-8 text-[#90ee90]" />
            <span className="font-bold text-xl font-['Manrope']">
              Sarthesha Farmers Producer Company
            </span>
          </div>
          
          <div className="text-center md:text-right">
            <p className="text-white/70 text-sm">
              Founded in 2024 • Maharashtra, India
            </p>
            <p className="text-white/50 text-xs mt-2">
              Empowering farmers with technology
            </p>
          </div>
        </div>
        
        <div className="border-t border-white/10 mt-8 pt-8 text-center">
          <p className="text-white/50 text-sm">
            © 2024 Sarthesha Farmers Producer Company. All rights reserved.
          </p>
        </div>
      </div>
    </footer>);

};

// Main App
function App() {
  return (
    <div className="App" data-testid="app-container">
      <Toaster position="top-right" richColors />
      <Navbar />
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <ImpactSection />
      <FoundersSection />
      <TestimonialsSection />
      <ContactSection />
      <Footer />
    </div>);

}

export default App;